import { ActionIcon, AspectRatio, Box, Button, Grid, Group, Image as ImageComponent, Input, List, Modal, Paper, rem, ScrollArea, SimpleGrid, Stack, Text, Title, Transition } from "@mantine/core";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { useDisclosure, useHover, useTimeout } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconCrop, IconCrosshair, IconFocus2, IconPhotoPlus, IconTrash, IconUpload, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { useDrop } from "react-dnd";
import { ImageFocalPoint, mapFocalPointReverse } from "./ImageFocalPoint";
import { CropEditor } from "@/Partials/AdEditor/Components/CropEditor";
import { useTranslation } from "react-i18next";
import { MAX_IMAGE_UPLOAD_SIZE, readableUploadErrors } from "./uploadLimits";
import { resolveImageSrc } from "@/lib";

export const FileUpload = ({
    label,
    value,
    onChange,
    withFocus = false,
    focusProps = {},
    handleImageSize = null,
    enableCrop = false,
    previewType = 'image', // 'image' renders an <img>; 'html' renders an <iframe> (for HTML-zip creatives)
    previewHeight = 170,
    paperProps = {},
    previewWrapperProps = {},
    imageProps = {},
    showActions = { crop: true, focus: true, remove: true },
    ...props
}) => {
    const [errors, setErrors] = useState([]);
    const errorTimeout = useTimeout(() => setErrors([]), 3000);

    const [{ canDrop, isOver }, drop] = useDrop(() => ({
        accept: "image",
        drop: async (item) => {

            if (!item?.src) return;
            onChange(item.src);

            if (typeof handleImageSize === "function") {
                const img = new Image();
                img.onload = () => {
                    handleImageSize({ width: img.width, height: img.height });
                };
                img.src = item.src;
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    }));

    const getPreviewUrl = () => resolveImageSrc(value);

    const { t } = useTranslation();

    const clearField = () => onChange(null);

    const deleteModal = () => modals.openConfirmModal({
        title: t('upload.deleteConfirm', { label }),
        labels: { confirm: t('common.yes'), cancel: t('common.cancel') },
        confirmProps: { color: 'red' },
        onConfirm: clearField,
    });

    const lightBoxImage = () => modals.open({
        title: value?.name || value?.path || t('upload.viewImage'),
        children: (
            <>
                <ImageComponent className="pattern-background" src={getPreviewUrl()} />
            </>
        ),
    });

    const lightBoxFocal = () => modals.open({
        title: t('upload.imageSetFocus'),
        size: "80%",
        children: (
            <ImageFocus preview={getPreviewUrl()} {...focusProps} />
        ),
    });

    return (
        <Box ref={drop}>
            <Input.Wrapper label={label}>
                {value && getPreviewUrl()
                    ?
                    <Paper p="sm" withBorder radius="md" {...paperProps}>
                        <div style={{ display: 'flex', ...(previewWrapperProps?.style || {}) }}>
                            {previewType === 'html'
                                ? (
                                    // HTML-zip creatives are documents, not images — render the
                                    // entry index.html in an iframe. An <img> can't display HTML
                                    // and would show a broken-image placeholder.
                                    <iframe
                                        scrolling="no"
                                        title={label || t('upload.htmlPreview')}
                                        src={getPreviewUrl()}
                                        style={{ width: '100%', height: rem(previewHeight), border: 0, ...(imageProps?.style || {}) }}
                                    />
                                )
                                : (
                                    <ImageComponent
                                        className="pattern-background"
                                        src={getPreviewUrl()}
                                        h={previewHeight}
                                        w="auto"
                                        maw="100%"
                                        fit="contain"
                                        onClick={lightBoxImage}
                                        style={{ cursor: 'pointer', ...(imageProps?.style || {}) }}
                                        {...imageProps}
                                    />
                                )}
                        </div>
                        <Group gap="xs">
                            {previewType !== 'html' && enableCrop && showActions.crop && (
                                <CropActionIcon src={getPreviewUrl()} onCrop={onChange} />
                            )}
                            {previewType !== 'html' && withFocus && showActions.focus && (
                                <ActionIcon component="button" type="button" mt="md" size="md" radius="md" variant="light" color="smartico-blue" onClick={lightBoxFocal} style={{ pointerEvents: 'all' }} title={t('upload.setFocus')}><IconFocus2 size={18} /></ActionIcon>
                            )}
                            {showActions.remove && (
                                <ActionIcon component="button" type="button" mt="md" size="md" radius="md" variant="light" color="red" onClick={deleteModal} style={{ pointerEvents: 'all' }} title={t('common.remove')}><IconTrash size={18} /></ActionIcon>
                            )}
                        </Group>
                    </Paper>
                    :
                    <Dropzone
                        onDrop={(files) => {
                            var reader = new FileReader();
                            reader.readAsDataURL(files[0]);
                            return reader.onload = () => onChange(reader.result)
                        }
                        }
                        maxSize={MAX_IMAGE_UPLOAD_SIZE}
                        accept={[
                            MIME_TYPES.png,
                            MIME_TYPES.jpeg,
                            MIME_TYPES.gif,
                            MIME_TYPES.webp,
                        ]}
                        {...props}
                        radius="md"
                        maxFiles={1}
                        multiple={false}
                        onReject={(errors) => {
                            const maxSize = props?.dropzoneProps?.maxSize ?? props.maxSize ?? MAX_IMAGE_UPLOAD_SIZE;
                            setErrors(readableUploadErrors(errors[0].errors, maxSize, t));
                            errorTimeout.start();
                        }}
                        {...props?.dropzoneProps}
                        {...(errors?.length ? { style: { borderColor: 'var(--mantine-color-red-6)' } } : {})}

                    >
                        <Group justify="center" gap="xl" mih={56} style={{ pointerEvents: 'none' }}>
                            <Dropzone.Accept>
                                <IconUpload
                                    style={{ width: rem(24), height: rem(24), color: 'var(--mantine-color-blue-6)' }}
                                    stroke={1.5}
                                />
                            </Dropzone.Accept>
                            <Dropzone.Reject>
                                <IconX
                                    style={{ width: rem(24), height: rem(24), color: 'var(--mantine-color-red-6)' }}
                                    stroke={1.5}
                                />
                            </Dropzone.Reject>
                            <Dropzone.Idle>

                                <Stack align="center" gap={2}>
                                    <IconPhotoPlus
                                        style={{ width: rem(24), height: rem(24), color: errors?.length ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-dimmed)' }}
                                        stroke={1.5}
                                    />
                                    <Text size="sm" c={errors?.length ? "red" : "dimmed"} inline>
                                        {t('upload.clickOrDrag')}
                                    </Text>
                                    <Text size="sm" c={errors?.length ? "red" : "dimmed"} inline>
                                        {t('upload.toUploadFile')}
                                    </Text>
                                </Stack>

                            </Dropzone.Idle>
                        </Group>
                    </Dropzone >
                }
            </Input.Wrapper>
            <List size="xs">
                {
                    errors?.map(({ message }, index) => <List.Item key={`error-${index}`} c="red">{message}</List.Item>)
                }
            </List>

        </Box>
    )
}

export const ImageFocus = ({ preview, valueX = 0, valueY = 0, onChangeX, onChangeY, onSave = () => { } }) => {

    const { t } = useTranslation();

    const [focalPoint, setFocalPoint] = useState({
        x: valueX,
        y: valueY
    });

    const ImageBox = ({ width = '100%', height = '100%', ratio = 1 }) => {

        const objectPosition = `${mapFocalPointReverse(focalPoint)?.x * 100}% ${mapFocalPointReverse(focalPoint)?.y * 100}%`;

        return (
            <AspectRatio ratio={ratio}>
                <Paper withBorder shadow="sm" radius={0} style={{ width: '100%', height: '100%' }}>
                    <img
                        src={preview}
                        alt={t('upload.focusImage')}
                        style={{
                            gridArea: 'square',
                            objectFit: 'cover',
                            width: width,
                            height: height,
                            objectPosition: objectPosition, // updated here directly
                        }}
                    />
                </Paper>
            </AspectRatio>
        );
    };

    const saveFocus = () => {
        onChangeX(focalPoint?.x);
        onChangeY(focalPoint?.y);
        modals.closeAll();
        onSave();
    }

    return (
        <>
            <Grid>
                <Grid.Col span={4}>
                    <Box px="lg">
                        <Title order={5} mb="md" fw={500}>{t('upload.setFocusArea')}</Title>
                        <ImageFocalPoint
                            src={preview}// Same src as <img> HTML element
                            height="100%"
                            focalPoint={focalPoint}
                            onChange={focalPoint => {
                                setFocalPoint(focalPoint)
                            }}
                        />
                        <Group mt="md">
                            <IconCrosshair size={72} stroke={1.5} />
                            <div>
                                <Text>x="{focalPoint.x}"</Text>
                                <Text>y="{focalPoint.y}"</Text>
                            </div>
                        </Group>

                    </Box>
                </Grid.Col>
                <Grid.Col span={8}>
                    <Box>
                        <Title order={5} mb="md" fw={500}>{t('upload.imageFocus')}</Title>
                    </Box>
                    <ScrollArea h="70vh">

                        <Stack gap="md">

                            <SimpleGrid cols={3}>
                                <ImageBox height="50%" />
                                <ImageBox width="50%" />
                                <ImageBox />
                            </SimpleGrid>
                            <ImageBox width="50%" ratio={4 / 1} />
                            <ImageBox ratio={4 / 1} />
                            <SimpleGrid cols={3} mb="md">
                                <ImageBox height="50%" ratio={1 / 2} />
                                <ImageBox />
                            </SimpleGrid>
                        </Stack>
                    </ScrollArea>
                </Grid.Col>

            </Grid>
            <Group justify="flex-end" mt="md">
                <Button onClick={modals.closeAll} color="gray" variant="outline" radius="md">{t('common.cancel')}</Button>
                <Button color="smartico-blue" radius="md" onClick={saveFocus}>{t('upload.saveFocus')}</Button>
            </Group>

        </>
    )
}


const CropActionIcon = ({ src, onCrop }) => {
    const { t } = useTranslation();

    const [opened, { open, close }] = useDisclosure(false);
    const { hovered, ref } = useHover();

    return (
        <>
            <Modal size="70vh" opened={opened} onClose={close} title={t('upload.cropEditor')} centered withinPortal>
                <div>
                    <Box pos="relative">
                        <CropEditor src={src} onCrop={(crop) => onCrop(crop?.toDataURL())} />
                    </Box>
                </div>

            </Modal>
            <Button
                ref={ref}
                onClick={open}
                mt="md"
                size="compact-md"
                radius="md"
                variant="light"
                color="smartico-blue"
                title={t('upload.cropEditor')}
                px={4}
                style={{
                    pointerEvents: 'all',
                    transition: 'all 0.3s ease',
                    width: hovered ? '80px' : '30px',
                    justifyContent: 'start',
                }}
            >
                <IconCrop size={22} />
                <Transition
                    mounted={hovered}
                    transition="fade-right"
                    duration={300}
                    timingFunction="ease"
                >
                    {(styles) => (
                        <div style={styles}>
                            <Text ml={2} fw="bold" fz="sm">{t('logo.crop')}</Text>
                        </div>
                    )}
                </Transition>
            </Button>
        </>
    )
}
